import { useState, useEffect } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { motion } from 'framer-motion';
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  Award,
  AlertCircle,
  CheckCircle,
  BarChart3,
  BookOpen,
  GraduationCap,
  Activity,
  Zap
} from 'lucide-react';
import AttendanceTips from './AttendanceTips';
import { getData } from '../../../../services/apiService';
import { useSelector } from 'react-redux';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-100">
        <p className="font-medium text-gray-800">{label}</p>
        {payload.map((item, index) => (
          <p key={`tooltip-${index}-${item.name}`} className="text-sm" style={{ color: item.color }}>
            {item.name}: {item.value}%
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AttendanceView = () => {
  const [attendanceData, setAttendanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Get user data from Redux
  const usersData = useSelector((state) => state.user);
  const user = usersData?.user || {};
  const dashboard = user?.dashboard || {};

  // Get values from user data
  const rollNo = user?.roll_no || dashboard?.rollNo || '24220921';
  const procYear = user?.procYear || dashboard?.procYear || 2024;
  const procSem = user?.procSem || dashboard?.procSem || 2;
  const semNo = user?.semNo || dashboard?.semNo || 2;

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response =
          //  {
          //   "status": true,
          //   "message": "Student Attendance Retrieved Successfully",
          //   "data": {
          //     "rollNo": "20100056",
          //     "procYear": 2024,
          //     "procSem": 2,
          //     "semNo": 2,
          //     "attendance": [
          //       {
          //         "month": "December",
          //         "presentHours": 90,
          //         "totalHours": 100,
          //         "percentage": 90
          //       },
          //       {
          //         "month": "January",
          //         "presentHours": 40,
          //         "totalHours": 100,
          //         "percentage": 40
          //       },
          //       {
          //         "month": "February",
          //         "presentHours": 70,
          //         "totalHours": 100,
          //         "percentage": 70
          //       },
          //       {
          //         "month": "March",
          //         "presentHours": 0,
          //         "totalHours": 0,
          //         "percentage": 0
          //       },
          //       {
          //         "month": "April",
          //         "presentHours": 0,
          //         "totalHours": 0,
          //         "percentage": 0
          //       },
          //       {
          //         "month": "May",
          //         "presentHours": 0,
          //         "totalHours": 0,
          //         "percentage": 0
          //       }
          //     ],
          //     "semesterSummary": {
          //       "presentHours": 200,
          //       "totalHours": 300,
          //       "percentage": 66.67
          //     }
          //   }
          // }

          await getData(
            `AttendanceDefaulters/GetStudentAttendance?procYear=${procYear}&procSem=${procSem}&rollNo=${rollNo}`
          );

        if (response?.status && response?.data) {
          setAttendanceData(response.data);
        } else {
          setError(response?.message || 'Failed to fetch attendance data');
        }
      } catch (err) {
        console.error('Error fetching attendance:', err);
        setError(err.message || 'An error occurred while fetching attendance');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendance();
  }, [rollNo, procYear, procSem]);

  const getPercentageColor = (percentage) => {
    if (percentage >= 85) return '#10B981';
    if (percentage >= 75) return '#F59E0B';
    if (percentage >= 65) return '#F97316';
    return '#EF4444';
  };

  const getStatusInfo = (percentage) => {
    if (percentage >= 75) {
      return { label: 'Good Standing', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
    } else if (percentage >= 65) {
      return { label: 'Needs Improvement', color: 'text-orange-600', bg: 'bg-orange-50', icon: AlertCircle };
    } else {
      return { label: 'At Risk', color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle };
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#0d4f5c]/20 border-t-[#0d4f5c]"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
          <div className="text-red-500 mb-4">
            <AlertCircle className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Attendance</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white rounded-lg hover:opacity-90 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!attendanceData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
          <div className="text-gray-400 mb-4">
            <BookOpen className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Attendance Data</h2>
          <p className="text-gray-600">No attendance records found for this student.</p>
        </div>
      </div>
    );
  }

  // Calculate stats from API data
  const { attendance, semesterSummary } = attendanceData;
  const totalAttended = semesterSummary?.presentHours || 0;
  const totalClasses = semesterSummary?.totalHours || 0;
  const overallPercentage = totalClasses > 0 ? Math.round((totalAttended / totalClasses) * 100) : 0;

  // Filter valid months (with totalHours > 0)
  const validMonths = attendance?.filter(m => m.totalHours > 0) || [];
  const hasData = validMonths.length > 0;

  // Find best and worst months
  const bestMonth = hasData ? validMonths.reduce((best, current) =>
    current.percentage > best.percentage ? current : best
  ) : { month: 'N/A', presentHours: 0, totalHours: 0, percentage: 0 };

  const worstMonth = hasData ? validMonths.reduce((worst, current) =>
    current.percentage < worst.percentage ? current : worst
  ) : { month: 'N/A', presentHours: 0, totalHours: 0, percentage: 0 };

  // Current month (last month with data or last in array)
  const currentMonth = hasData ? validMonths[validMonths.length - 1] : attendance?.[attendance.length - 1] || { month: 'N/A', presentHours: 0, totalHours: 0, percentage: 0 };
  const currentMonthPercentage = currentMonth.totalHours > 0 ? Math.round(currentMonth.percentage) : 0;

  const statusInfo = getStatusInfo(overallPercentage);
  const StatusIcon = statusInfo.icon;

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header Section */}
          <motion.div
            variants={cardVariants}
            className="mb-8 bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] rounded-2xl p-6 text-white shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Attendance Dashboard</h1>
                    <p className="text-blue-100 text-sm">Roll No: {attendanceData.rollNo || rollNo} | Semester {attendanceData.semNo || semNo}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-200" />
                  <span className="text-sm">{attendanceData.procYear || procYear}</span>
                </div>
                <div className="w-px h-6 bg-white/20"></div>
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-200" />
                  <span className="text-sm">Sem {attendanceData.semNo || semNo}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Summary Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Overall Attendance */}
            <motion.div
              variants={cardVariants}
              whileHover="hover"
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all duration-300 hover:shadow-xl"
              onMouseEnter={() => setHoveredCard('overall')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Overall</h3>
                <div className={`p-2 rounded-lg ${statusInfo.bg}`}>
                  <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20">
                  <CircularProgressbar
                    value={overallPercentage}
                    text={`${overallPercentage}%`}
                    styles={buildStyles({
                      pathColor: getPercentageColor(overallPercentage),
                      textColor: '#1F2937',
                      textSize: '18px',
                      trailColor: '#E5E7EB',
                      strokeLinecap: 'round',
                    })}
                  />
                  {hoveredCard === 'overall' && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1"
                    >
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    </motion.div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    {totalAttended} / {totalClasses} hrs
                  </p>
                  <p className={`text-sm font-semibold ${statusInfo.color}`}>
                    {statusInfo.label}
                  </p>
                  <div className="mt-1 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${overallPercentage}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-full rounded-full`}
                      style={{ backgroundColor: getPercentageColor(overallPercentage) }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Best Month */}
            <motion.div
              variants={cardVariants}
              whileHover="hover"
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all duration-300 hover:shadow-xl"
              onMouseEnter={() => setHoveredCard('best')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Best Month</h3>
                <div className="p-2 bg-green-50 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16">
                  <CircularProgressbar
                    value={hasData ? Math.round(bestMonth.percentage) : 0}
                    text={hasData ? `${Math.round(bestMonth.percentage)}%` : 'N/A'}
                    styles={buildStyles({
                      pathColor: '#10B981',
                      textColor: '#1F2937',
                      textSize: '14px',
                      trailColor: '#E5E7EB',
                      strokeLinecap: 'round',
                    })}
                  />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{hasData ? bestMonth.month : 'No data'}</p>
                  <p className="text-sm text-gray-500">
                    {hasData ? `${bestMonth.presentHours}/${bestMonth.totalHours} hrs` : '-'}
                  </p>
                  {hasData && (
                    <div className="flex items-center gap-1 mt-1">
                      <Award className="h-3 w-3 text-yellow-500" />
                      <span className="text-xs text-gray-500">Best performance</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Month to Improve */}
            <motion.div
              variants={cardVariants}
              whileHover="hover"
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all duration-300 hover:shadow-xl"
              onMouseEnter={() => setHoveredCard('worst')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Needs Improvement</h3>
                <div className="p-2 bg-red-50 rounded-lg">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16">
                  <CircularProgressbar
                    value={hasData ? Math.round(worstMonth.percentage) : 0}
                    text={hasData ? `${Math.round(worstMonth.percentage)}%` : 'N/A'}
                    styles={buildStyles({
                      pathColor: '#EF4444',
                      textColor: '#1F2937',
                      textSize: '14px',
                      trailColor: '#E5E7EB',
                      strokeLinecap: 'round',
                    })}
                  />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{hasData ? worstMonth.month : 'No data'}</p>
                  <p className="text-sm text-gray-500">
                    {hasData ? `${worstMonth.presentHours}/${worstMonth.totalHours} hrs` : '-'}
                  </p>
                  {hasData && (
                    <div className="flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3 w-3 text-red-500" />
                      <span className="text-xs text-gray-500">Needs attention</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Current Month */}
            <motion.div
              variants={cardVariants}
              whileHover="hover"
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all duration-300 hover:shadow-xl"
              onMouseEnter={() => setHoveredCard('current')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Current Month</h3>
                <div className="p-2 bg-[#0d4f5c]/10 rounded-lg">
                  <Activity className="h-4 w-4 text-[#0d4f5c]" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16">
                  <CircularProgressbar
                    value={currentMonthPercentage}
                    text={currentMonth.totalHours > 0 ? `${currentMonthPercentage}%` : 'N/A'}
                    styles={buildStyles({
                      pathColor: currentMonth.totalHours > 0 ? '#0d4f5c' : '#D1D5DB',
                      textColor: '#1F2937',
                      textSize: '14px',
                      trailColor: '#E5E7EB',
                      strokeLinecap: 'round',
                    })}
                  />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{currentMonth.month}</p>
                  <p className="text-sm text-gray-500">
                    {currentMonth.totalHours > 0 ? `${currentMonth.presentHours}/${currentMonth.totalHours} hrs` : 'No data'}
                  </p>
                  {currentMonth.totalHours > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Zap className="h-3 w-3 text-[#0d4f5c]" />
                      <span className="text-xs text-gray-500">Current month</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Charts Section - Only show if there's data */}
          {hasData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <motion.div
                variants={cardVariants}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Monthly Attendance Overview</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <BarChart3 className="h-4 w-4" />
                    <span>By month</span>
                  </div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attendance}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E7EB' }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E7EB' }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="percentage"
                        name="Attendance %"
                        radius={[4, 4, 0, 0]}
                      >
                        {attendance.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.totalHours > 0 ? getPercentageColor(entry.percentage) : '#E5E7EB'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              <motion.div
                variants={cardVariants}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Attendance Trend</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <TrendingUp className="h-4 w-4" />
                    <span>Progression</span>
                  </div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={attendance}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E7EB' }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E7EB' }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="percentage"
                        name="Attendance %"
                        stroke="#0d4f5c"
                        strokeWidth={3}
                        dot={(props) => {
                          const { cx, cy, payload, index } = props;
                          const isActive = payload.totalHours > 0;
                          return isActive ? (
                            <circle
                              key={`dot-${index}-${payload.month}`}  // Using index + month for uniqueness
                              cx={cx}
                              cy={cy}
                              r={5}
                              fill="#0d4f5c"
                              strokeWidth={2}
                            />
                          ) : (
                            <circle
                              key={`dot-${index}-${payload.month}`}  // Same key pattern for both
                              cx={cx}
                              cy={cy}
                              r={3}
                              fill="#D1D5DB"
                              strokeWidth={1}
                            />
                          );
                        }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            </div>
          )}

          {/* Month-wise Attendance Details */}
          <motion.div
            variants={cardVariants}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Month-wise Attendance</h3>
                <p className="text-sm text-gray-500">Detailed monthly attendance summary</p>
              </div>
              <div className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] px-3 py-1 rounded-full text-sm text-white">
                Semester {attendanceData.semNo || semNo}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Month</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Present Hours</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Total Hours</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Percentage</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((item, index) => {
                    const percentage = item.totalHours > 0 ? Math.round(item.percentage) : 0;
                    const isActive = item.totalHours > 0;

                    return (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${!isActive ? 'opacity-50' : ''}`}
                      >
                        <td className="py-3 px-4 text-sm font-medium text-gray-800">
                          {item.month}
                        </td>
                        <td className="py-3 px-4 text-sm text-center text-gray-700">
                          {item.presentHours}
                        </td>
                        <td className="py-3 px-4 text-sm text-center text-gray-700">
                          {item.totalHours}
                        </td>
                        <td className="py-3 px-4 text-sm text-center">
                          <span className={`font-semibold ${isActive ? (percentage >= 75 ? 'text-green-600' : percentage >= 65 ? 'text-orange-600' : 'text-red-600') : 'text-gray-400'}`}>
                            {isActive ? `${percentage}%` : '-'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {isActive ? (
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${percentage >= 75 ? 'bg-green-100 text-green-700' :
                                percentage >= 65 ? 'bg-orange-100 text-orange-700' :
                                  'bg-red-100 text-red-700'
                              }`}>
                              {percentage >= 75 ? '✅ Good' :
                                percentage >= 65 ? '⚠️ Average' :
                                  '❌ Needs Improvement'}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No data</span>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gradient-to-r from-[#0d4f5c]/5 to-[#1a7a8a]/5">
                    <td className="py-3 px-4 text-sm font-semibold text-gray-800">Semester Summary</td>
                    <td className="py-3 px-4 text-sm text-center font-semibold text-gray-800">
                      {semesterSummary?.presentHours || 0}
                    </td>
                    <td className="py-3 px-4 text-sm text-center font-semibold text-gray-800">
                      {semesterSummary?.totalHours || 0}
                    </td>
                    <td className="py-3 px-4 text-sm text-center font-semibold">
                      <span className={semesterSummary?.percentage >= 75 ? 'text-green-600' : semesterSummary?.percentage >= 65 ? 'text-orange-600' : 'text-red-600'}>
                        {semesterSummary?.percentage || 0}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${semesterSummary?.percentage >= 75 ? 'bg-green-100 text-green-700' :
                          semesterSummary?.percentage >= 65 ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                        }`}>
                        {semesterSummary?.percentage >= 75 ? '✅ Eligible' :
                          semesterSummary?.percentage >= 65 ? '⚠️ Conditional' :
                            '❌ At Risk'}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </motion.div>

          {/* Attendance Guidelines */}
          <AttendanceTips />
        </motion.div>
      </div>
    </div>
  );
};

export default AttendanceView;  